import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import Modal from 'react-modal';
import Header from '../components/header';
import {
  ChatContainer,
  Sidebar,
  MainChat,
  InfoPanel,
  UserList,
  UserItem,
  MessageList,
  MessageItem,
  InputContainer,
  MessageInput,
  SendButton,
  GroupButton,
  NotificationIcon,
  GroupItem,
  ReplyContainer,
  AttachButton,
  FilePreviewChip,
  AttachmentCard
} from '../styles/chatStyles2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api, { searchMessages, searchGroupMessages } from '../api';
import GroupParticipantsModal from './GroupParticipantsModal';
import SearchComponent from '../components/SearchComponent';
import { FaSearch, FaPaperclip, FaFilePdf, FaFileWord, FaFileExcel, FaFileAlt, FaTimes } from 'react-icons/fa';

// Tipos de documento permitidos y límite (deben coincidir con el backend)
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

// Formatea el tamaño en unidades legibles
const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Icono según el tipo/extensión del documento
const getFileIcon = (fileType = '', fileName = '') => {
  const name = fileName.toLowerCase();
  if (fileType.includes('pdf') || name.endsWith('.pdf')) return <FaFilePdf color="#e74c3c" />;
  if (fileType.includes('word') || name.endsWith('.doc') || name.endsWith('.docx')) return <FaFileWord color="#2b579a" />;
  if (fileType.includes('excel') || fileType.includes('spreadsheet') || name.endsWith('.xls') || name.endsWith('.xlsx')) return <FaFileExcel color="#217346" />;
  return <FaFileAlt color="#666" />;
};


const apiUrl = process.env.REACT_APP_API_URL;
const apiUrlSocket = process.env.REACT_APP_API_URL_SOCKET;

const socket = io(`${apiUrlSocket}`, {
  transports: ['websocket'],
  auth: {
    token: localStorage.getItem('authToken'),
  },
});

Modal.setAppElement('#root'); // Necesario para accesibilidad

function ChatPage() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [authenticatedUserId, setAuthenticatedUserId] = useState(() => parseInt(localStorage.getItem('userId'), 10));
  const [isModalOpen, setIsModalOpen] = useState(false); // Estado para manejar la visibilidad del modal
  const [groupName, setGroupName] = useState(''); // Estado para el nombre del grupo
  const [selectedGroupUsers, setSelectedGroupUsers] = useState([]); // Estado para los usuarios seleccionados en el grupo
  const [userGroups, setUserGroups] = useState([]); // Estado para los grupos a los que pertenece el usuario
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [unreadUsers, setUnreadUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadGroups, setUnreadGroups] = useState([]);
  const messagesEndRef = useRef();
  const messageListRef = useRef(); // Referencia para el contenedor de mensajes
  const [offset, setOffset] = useState(0); // Define offset en el estado
  const limit = 200;
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isFetchingOldMessages, setIsFetchingOldMessages] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isGroupParticipantsModalOpen, setIsGroupParticipantsModalOpen] = useState(false);
  const [groupParticipants, setGroupParticipants] = useState([]);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null); // Documento adjunto pendiente de enviar
  const fileInputRef = useRef(null);

  // Validar y guardar el documento seleccionado
  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error('Tipo de archivo no permitido. Solo PDF, Word, Excel o TXT.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('El archivo supera el límite de 10 MB.');
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };


  useEffect(() => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    // Obtener usuarios
    api.get('/api/users')
      .then(response => setUsers(response.data))
      .catch(error => console.error('Error fetching users:', error));

    // Obtener grupos a los que pertenece el usuario autenticado
    api.get(`/api/group/user/${authenticatedUserId}`)
      .then(response => setUserGroups(response.data))
      .catch(error => console.error('Error fetching groups:', error));

  }, [navigate, authenticatedUserId]);


  useEffect(() => {
    if (selectedUser) {
      setSelectedGroup(null);
      setMessages([]);
      setSearchResults([]);
      setIsSearchOpen(false);

      api.get(`/api/chat/${selectedUser.id}`)
        .then(response => {
          setMessages(response.data.map(msg => ({
            ...msg,
            isSentByAuthenticatedUser: msg.sender_id === authenticatedUserId
          })));
          // Scroll después de cargar mensajes
          setTimeout(() => scrollToBottom(), 100);
        })
        .catch(error => console.error('Error fetching messages:', error));
    }


  }, [selectedUser, authenticatedUserId]);



  useEffect(() => {
    if (selectedUser && authenticatedUserId) {
      const senderId = selectedUser.id;

      api.post(`/api/chat/notifications/${authenticatedUserId}`, { senderId })
        .then(() => {
          // Opcional: Actualiza la lista de usuarios con notificaciones no leídas si es necesario
          setUnreadUsers(prevUnreadUsers => prevUnreadUsers.filter(id => id !== senderId));
        })
        .catch(error => console.error('Error marking notifications as read:', error));
    }
  }, [selectedUser, authenticatedUserId]);

  useEffect(() => {
    if (selectedUser) {
      // Unir al usuario autenticado a la sala de conversación
      socket.emit('join_room', { userId: authenticatedUserId, roomId: selectedUser.id });
      console.log(`User ${authenticatedUserId} joined room ${selectedUser.id}`);

      // Salir de la sala anterior si es necesario
      return () => {
        socket.emit('leave_room', { userId: authenticatedUserId, roomId: selectedUser.id });
        console.log(`User ${authenticatedUserId} left room ${selectedUser.id}`);
      };
    }
  }, [selectedUser, authenticatedUserId]);

  useEffect(() => {
    if (!selectedUser) return;

    const handleNewMessage = (message) => {
      console.log('Received message:', message);

      // Solo agregar mensajes que no fueron enviados por el usuario autenticado
      if (
        (message.receiver_id === authenticatedUserId && message.sender_id === selectedUser.id) ||
        (message.sender_id === authenticatedUserId && message.receiver_id === selectedUser.id)
      ) {
        // Agregar el flag para identificar si el mensaje fue enviado por el usuario autenticado
        const messageWithAuthFlag = {
          ...message,
          isSentByAuthenticatedUser: message.sender_id === authenticatedUserId,
        };

        // Agregar el mensaje al estado
        setMessages(prevMessages => [...prevMessages, messageWithAuthFlag]);
      } else {
        console.log('Message does not match the current chat context');
      }
    };

    socket.off('new_message', handleNewMessage);
    socket.on('new_message', handleNewMessage);

    return () => {
      socket.off('new_message', handleNewMessage);
    };
  }, [selectedUser, authenticatedUserId]);


  useEffect(() => {
    const handleNewNotification = (notification) => {
      console.log('New notification received:', notification);

      // Verificar si la notificación es para el usuario autenticado
      // y que el sender_id no esté seleccionado en el chat actual
      if (
        notification.receiver_id === authenticatedUserId &&
        (!selectedUser || notification.sender_id !== selectedUser.id)
      ) {
        // Agregar el sender_id a la lista de usuarios con mensajes no leídos
        setUnreadUsers(prevUnreadUsers => {
          if (!prevUnreadUsers.includes(notification.sender_id)) {
            return [...prevUnreadUsers, notification.sender_id];
          }
          return prevUnreadUsers;
        });


      }
    };

    socket.on('new_notification', handleNewNotification);

    return () => {
      socket.off('new_notification', handleNewNotification);
    };
  }, [authenticatedUserId, selectedUser]);


  const handleSendMessage = () => {
    // Debe haber texto o un documento adjunto
    if (newMessage.trim() === '' && !selectedFile) return;

    const selectedUserId = parseInt(selectedUser.id, 10);

    let request;
    if (selectedFile) {
      const form = new FormData();
      if (newMessage.trim() !== '') form.append('content', newMessage);
      if (replyingTo) form.append('replyTo', replyingTo.id);
      form.append('file', selectedFile);
      request = api.post(`/api/chat/${selectedUserId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else {
      request = api.post(`/api/chat/${selectedUserId}`, {
        content: newMessage,
        replyTo: replyingTo ? replyingTo.id : null,
      });
    }

    request
      .then(response => {
        if (response.data && response.data.message) {
          // El propio remitente recibe el mensaje vía socket (new_message),
          // por lo que solo limpiamos el formulario aquí.
          setNewMessage('');
          setReplyingTo(null);
          clearSelectedFile();
        }
      })
      .catch(error => {
        console.error('Error sending message:', error);
        toast.error(error?.response?.data?.error || 'Error al enviar el mensaje');
      });
  };

  const handleCreateGroup = () => {
    if (groupName.trim() === '' || selectedGroupUsers.length === 0) return;

    api.post('/api/group/create', {
      name: groupName,
      users: [...selectedGroupUsers, authenticatedUserId], // Añade el ID del usuario autenticado
    })
      .then(response => {
        console.log('Group created:', response.data);
        // Resetea los estados después de crear el grupo
        setGroupName('');
        setSelectedGroupUsers([]);
        setIsModalOpen(false); // Cierra el modal

        return api.get(`/api/group/user/${authenticatedUserId}`);
      })
      .then(response => {
        console.log('Groups updated:', response.data);
        setUserGroups(response.data);
      })
      .catch(error => console.error('Error creating group:', error));
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleGroupUserSelection = (userId) => {
    if (selectedGroupUsers.includes(userId)) {
      setSelectedGroupUsers(selectedGroupUsers.filter(id => id !== userId));
    } else {
      setSelectedGroupUsers([...selectedGroupUsers, userId]);
    }
  };

  useEffect(() => {

    console.log('useEffect mounted');
    const handleGroupCreated = (groupData) => {
      console.log('Received group creation event:', groupData);

      // Actualizar el estado de los grupos
      setUserGroups(prevGroups => {
        const updatedGroups = [...prevGroups, groupData];
        console.log('After update:', updatedGroups);
        return updatedGroups;
      });
    };

    socket.on('group_created', handleGroupCreated);
    console.log('Subscribed to group_created event');

    return () => {
      socket.off('group_created', handleGroupCreated);
    };
    // Quitar userGroups de las dependencias
  }, [userGroups]);  // 

  //Funcion trae mensajes grupos
  const fetchMessages = async (newOffset = 0) => {
    try {
      // Guarda la posición actual del scroll y la altura antes de la carga de mensajes
      const previousScrollHeight = messageListRef.current.scrollHeight;
      const previousScrollTop = messageListRef.current.scrollTop;

      // Realiza la solicitud para obtener mensajes antiguos
      const response = await api.get(`/api/group/${selectedGroup}`, {
        params: {
          offset: newOffset,
          limit: limit
        }
      });

      // Verifica si hay más mensajes para cargar
      if (response.data.length < limit) setHasMoreMessages(false);

      // Ordena los mensajes por fecha (de más antiguo a más reciente)
      const sortedMessages = response.data.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      // Agregar los nuevos mensajes al final (más recientes al final)
      setMessages(prevMessages => [
        ...prevMessages,
        ...sortedMessages, // Los mensajes ordenados por fecha
      ]);

      // Restaura la posición del scroll para que no cambie
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight - previousScrollHeight + previousScrollTop;
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };




  // Definir handleScroll aquí para asegurar que esté en el mismo contexto
  const handleScroll = async (e) => {
    if (e.target.scrollTop === 0 && hasMoreMessages && !isFetchingOldMessages) {
      setIsFetchingOldMessages(true);
      const newOffset = offset + limit;
      await fetchMessages(newOffset);
      setOffset(newOffset);
      setIsFetchingOldMessages(false);
    }
  };

  // useEffect para cargar mensajes al seleccionar un grupo
  useEffect(() => {
    if (selectedGroup) {
      setSelectedUser(null);
      setSelectedUser(null);
      setMessages([]);
      setSearchResults([]);
      setIsSearchOpen(false);
      setHasMoreMessages(true);
      setOffset(0); // Reiniciar el offset al seleccionar un nuevo grupo
      fetchMessages(0).then(() => {
        // Scroll después de cargar mensajes del grupo
        setTimeout(() => scrollToBottom(), 100);
      });
    }
  }, [selectedGroup]);

  // useEffect para manejar el evento de scroll
  /*useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (messageListRef.current) {
        messageListRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [hasMoreMessages, offset]);;*/

  // Auto-scroll al fondo cuando se envían o cargan mensajes nuevos
  useEffect(() => {
    // Auto-scroll solo si no se están cargando mensajes antiguos y hay mensajes nuevos
    if (!isFetchingOldMessages && !editingMessageId) {
      scrollToBottom();
    }
  }, [messages, isFetchingOldMessages, editingMessageId]);

  //Join Group
  useEffect(() => {
    if (selectedGroup) {
      // Unir al usuario autenticado a la sala de conversación
      socket.emit('join_room', { userId: authenticatedUserId, roomId: selectedGroup });
      console.log(`User ${authenticatedUserId} joined room group ${selectedGroup}`);

      // Salir de la sala anterior si es necesario
      return () => {
        socket.emit('leave_room', { userId: authenticatedUserId, roomId: selectedGroup });
        console.log(`User ${authenticatedUserId} left room group ${selectedGroup}`);
      };
    }
  }, [selectedGroup, authenticatedUserId]);


  useEffect(() => {
    const handleNewGroupMessage = (message) => {
      console.log('Received message:', message);
      console.log('este es:', selectedGroup);
      console.log('este otro:', message.groupId);
      if (message.groupId === selectedGroup) {

        const messageWithAuthFlag = {
          ...message,
          isSentByAuthenticatedUser: message.sender_id === authenticatedUserId,
        };

        setMessages(prevMessages => [...prevMessages, messageWithAuthFlag]);
      } else {
        // Si el mensaje es para otro grupo, maneja la notificación interna
        console.log('Message does not match the current chat group context');

        // Agrega el groupId a la lista de grupos con mensajes no leídos si no está seleccionado
        setUnreadGroups(prevUnreadGroups => {
          if (!prevUnreadGroups.includes(message.groupId)) {
            return [...prevUnreadGroups, message.groupId];
          }
          return prevUnreadGroups;
        });
      }
    };

    socket.off('new_message', handleNewGroupMessage);
    socket.on('new_message', handleNewGroupMessage);

    return () => {
      socket.off('new_message', handleNewGroupMessage);
    };
  }, [selectedGroup]);

  const handleSendGroupMessage = () => {
    // Debe haber texto o un documento adjunto
    if (newMessage.trim() === '' && !selectedFile) return;

    let request;
    if (selectedFile) {
      const form = new FormData();
      form.append('sender_id', authenticatedUserId);
      form.append('groupId', selectedGroup);
      if (newMessage.trim() !== '') form.append('content', newMessage);
      if (replyingTo) form.append('replyTo', replyingTo.id);
      form.append('file', selectedFile);
      request = api.post('/api/group/send', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else {
      request = api.post('/api/group/send', {
        sender_id: authenticatedUserId,
        groupId: selectedGroup,
        content: newMessage,
        replyTo: replyingTo ? replyingTo.id : null,
      });
    }

    request
      .then(response => {
        if (response.data && response.data.message) {
          // El mensaje se pinta vía socket (new_message del grupo); aquí solo limpiamos.
          setNewMessage('');
          setReplyingTo(null);
          clearSelectedFile();
        }
      })
      .catch(error => {
        console.error('Error sending group message:', error);
        toast.error(error?.response?.data?.error || 'Error al enviar el mensaje');
      });
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  /* useEffect(() => {

     // Auto-scroll to the bottom when messages change
     if (messagesEndRef.current) {
       messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
     }
   }, [selectedGroup]);*/

  useEffect(() => {
    // Obtener notificaciones no leídas al cargar el componente
    const fetchUnreadNotifications = async () => {
      try {
        const response = await api.get(`/api/chat/notifications/${authenticatedUserId}`);
        const data = response.data;
        const unreadUserIds = data.map(notification => notification.sender_id);

        // Eliminar duplicados en el array de IDs
        const uniqueUnreadUserIds = [...new Set(unreadUserIds)];
        setUnreadUsers(uniqueUnreadUserIds);
      } catch (error) {
        console.error('Error fetching unread notifications:', error);
      }
    };

    fetchUnreadNotifications();
  }, [authenticatedUserId]);

  useEffect(() => {
    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
    };
  }, [selectedUser, authenticatedUserId]);


  const getUsernameById = (id) => {
    const user = users.find(u => u.id === id);
    return user ? user.name : 'Unknown';
  };

  Notification.requestPermission().then(permission => {
    if (permission === 'granted') {
      console.log('Permission for notifications granted');
      // Llama aquí al registro del Service Worker y la suscripción a las notificaciones
      subscribeUserToPush();
    } else {
      console.warn('Permission for notifications denied');
    }
  });

  const subscribeUserToPush = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js').then(registration => {
        console.log('Service Worker registrado con éxito:', registration);

        const publicVapidKey = 'BHpwZWZua-3kyCqSzs60eqWWaPz151eF8n5vdy7X8ahpZ8ev3wpbSMH9kAybmuIeMKHGJdZHsC863g-8rd54c20'; // Reemplazar con tu clave pública VAPID

        return registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicVapidKey),
        });
      }).then(subscription => {
        console.log('Suscripción a Push Notifications:', subscription);

        // Enviar la suscripción al servidor para almacenarla
        fetch(`${apiUrl}/api/chat/push/notification`, {
          method: 'POST',
          body: JSON.stringify({ subscription: subscription.toJSON() }),
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
        })
          .then(response => {
            console.log('Response status:', response.status);
            console.log('Response Content-Type:', response.headers.get('Content-Type'));
            return response.text(); // Asegúrate de devolver la respuesta como texto
          })
          .then(text => {
            console.log('Response text:', text);
            try {
              const data = JSON.parse(text);
              console.log('Suscripción enviada al servidor:', data);

              // Marcar al usuario como suscrito en localStorage
              localStorage.setItem('isPushSubscribed', 'true');
            } catch (e) {
              console.error('Error al parsear JSON:', e);
            }
          })
          .catch(error => console.error('Error al enviar la suscripción:', error));
      }).catch(error => {
        console.error('Error en la suscripción a notificaciones:', error);
      });
    }
  };

  useEffect(() => {
    const isPushSubscribed = localStorage.getItem('isPushSubscribed');

    // Verificar si ya está suscrito para evitar ejecutar el código nuevamente
    if (!isPushSubscribed) {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          subscribeUserToPush();
        } else {
          console.warn('Permission for notifications denied');
        }
      });
    }
  }, [authenticatedUserId]);  // Se ejecuta después del login



  // Función de ayuda para convertir la clave VAPID en Uint8Array
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  }

  useEffect(() => {
    if (selectedGroup) {
      const groupId = selectedGroup; // Aquí asumes que `selectedGroup` tiene un `id`

      // Llamada a la API para marcar las notificaciones como leídas
      api.post('/api/group/notification/read', { groupId })
        .then(() => {
          // Actualiza la lista de grupos con notificaciones no leídas si es necesario
          setUnreadGroups(prevUnreadGroups => prevUnreadGroups.filter(id => id !== groupId));
        })
        .catch(error => console.error('Error marking group notifications as read:', error));
    }
  }, [selectedGroup]);

  useEffect(() => {
    const fetchUnreadGroupNotifications = async () => {
      if (!userGroups || userGroups.length === 0) return; // Verifica que haya grupos

      try {
        const unreadGroupIds = []; // Array para almacenar IDs de grupos con notificaciones no leídas

        // Itera sobre los grupos del usuario y busca las notificaciones no leídas para cada uno
        for (const group of userGroups) {
          const { data } = await api.get(`/api/group/notification/unRead/${group.id}`); // Utilizamos la función get de api.js

          // Si hay notificaciones para el grupo, añade el ID a la lista
          if (data && data.length > 0) {
            unreadGroupIds.push(group.id);
          }
        }

        // Actualiza el estado de unreadGroups
        setUnreadGroups(unreadGroupIds);
      } catch (error) {
        console.error('Error fetching unread group notifications:', error);
      }
    };

    if (userGroups.length > 0) {
      fetchUnreadGroupNotifications();
    }
  }, [userGroups]);


  useEffect(() => {
    const tokenRefreshInterval = setInterval(async () => {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          console.warn("No refresh token found, redirecting to login");
          localStorage.clear();
          window.location.href = '/login';
          return;
        }

        const response = await api.post('/api/auth/refresh-token', { refreshToken });
        const { accessToken, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem('authToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
      } catch (error) {
        console.error('Error al renovar el token:', error);
        localStorage.clear();
        window.location.href = '/login';
      }
    }, 10 * 60 * 1000); // Cada 10 minutos

    return () => clearInterval(tokenRefreshInterval); // Limpiar al desmontar el componente
  }, []);

  const handleRefreshAppData = async () => {
    setIsRefreshing(true);
    try {
      // 1. Obtener todos los usuarios
      const usersResponse = await api.get('/api/users');
      setUsers(usersResponse.data);

      // 2. Obtener los grupos del usuario autenticado
      const groupsResponse = await api.get(`/api/group/user/${authenticatedUserId}`);
      setUserGroups(groupsResponse.data);

      // 3. Obtener notificaciones no leídas de usuarios
      const notifsResponse = await api.get(`/api/chat/notifications/${authenticatedUserId}`);
      const unreadUserIds = notifsResponse.data.map(notification => notification.sender_id);
      setUnreadUsers([...new Set(unreadUserIds)]);

      // 4. Obtener notificaciones no leídas de grupos
      const unreadGroupIds = [];
      for (const group of groupsResponse.data) {
        const { data } = await api.get(`/api/group/notification/unRead/${group.id}`);
        if (data && data.length > 0) {
          unreadGroupIds.push(group.id);
        }
      }
      setUnreadGroups(unreadGroupIds);

      toast.success("Refresh successful");
    } catch (error) {
      console.error('Error Refresg:', error);
      toast.error("Hubo un error al actualizar la información");
    } finally {
      setIsRefreshing(false);
    }

  };

  const handleOpenGroupParticipants = async (group) => {
    try {
      const response = await api.get(`/api/group/details/${group.id}`); // debe devolver users
      setGroupParticipants(response.data.users || []);
      setSelectedGroupName(group.name);
      setIsGroupParticipantsModalOpen(true);
    } catch (error) {
      console.error('Error fetching group participants:', error);
    }
  };

  // Función para iniciar edición
  const handleStartEdit = (message) => {
    setEditingMessageId(message.id);
    setEditedContent(message.content);
  };

  // Función para cancelar edición
  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditedContent('');
  };

  // Función para guardar edición
  const handleSaveEdit = async (messageId) => {
    if (editedContent.trim() === '') {
      toast.error('El mensaje no puede estar vacío');
      return;
    }

    try {
      const endpoint = selectedGroup
        ? `/api/group/message/${messageId}`
        : `/api/chat/message/${messageId}`;

      const response = await api.put(endpoint, { content: editedContent });

      if (response.data) {
        // Emitir via socket
        const roomId = selectedGroup || selectedUser.id;
        socket.emit('edit_message', {
          messageId,
          content: editedContent,
          roomId,
          isGroup: !!selectedGroup
        });

        // Actualizar localmente
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.id === messageId
              ? { ...msg, content: editedContent, is_edited: true, edited_at: new Date() }
              : msg
          )
        );

        setEditingMessageId(null);
        setEditedContent('');
        toast.success('Message successfully edited');
      }
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error('Error editing message');
    }
  };

  // Función para borrar un mensaje con archivo (soft delete)
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;

    try {
      const endpoint = selectedGroup
        ? `/api/group/message/${messageId}`
        : `/api/chat/message/${messageId}`;

      await api.delete(endpoint);

      // Actualizar localmente: marcar como borrado y limpiar campos de archivo
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === messageId
            ? { ...msg, is_deleted: true, content: '', file_url: null, file_name: null, file_type: null, file_size: null }
            : msg
        )
      );

      toast.success('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Error deleting message');
    }
  };

  // Listener para ediciones de otros usuarios
  useEffect(() => {
    const handleMessageEdited = (data) => {
      const { messageId, content, edited_at } = data;

      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === messageId
            ? { ...msg, content, is_edited: true, edited_at }
            : msg
        )
      );
    };

    socket.on('message_edited', handleMessageEdited);

    return () => {
      socket.off('message_edited', handleMessageEdited);
    };
  }, []);

  // Listener para borrados (propios y de otros usuarios)
  useEffect(() => {
    const handleMessageDeleted = ({ messageId }) => {
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === messageId
            ? { ...msg, is_deleted: true, content: '', file_url: null, file_name: null, file_type: null, file_size: null }
            : msg
        )
      );
    };

    socket.on('message_deleted', handleMessageDeleted);

    return () => {
      socket.off('message_deleted', handleMessageDeleted);
    };
  }, []);

  const handleSearch = async (query) => {
    setIsSearching(true);
    try {
      let results = [];
      if (selectedUser) {
        results = await searchMessages(selectedUser.id, query);
      } else if (selectedGroup) {
        results = await searchGroupMessages(selectedGroup, query);
      }
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Error searching messages');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <ChatContainer>
      {/* Aquí se muestra el Header en la parte superior */}

      <Header />

      <div style={{ display: 'flex', flex: 1 }}>
        {/* Lista de usuarios */}
        <Sidebar>
          <button
            onClick={handleRefreshAppData}
            disabled={isRefreshing}
            style={{
              marginBottom: '10px',
              alignSelf: 'flex-end',
              backgroundColor: '#0078d4',
              color: 'white',
              padding: '8px 12px',
              border: 'none',
              borderRadius: '4px',
              cursor: isRefreshing ? 'not-allowed' : 'pointer'
            }}
          >
            {isRefreshing ? 'Updating...' : '🔄 Refresh'}
          </button>
          <h2>Users</h2>
          <UserList>
            {/* Activos */}
            {users
              .filter(user => user.status === 1)
              .map(user => (
                <UserItem
                  key={user.id}
                  user={user}
                  onClick={() => setSelectedUser(user)}
                  selected={selectedUser && selectedUser?.id === user.id}
                  isNotified={unreadUsers.includes(user.id)}
                >
                  {user.name}
                  <NotificationIcon isNotified={unreadUsers.includes(user.id)} />
                </UserItem>
              ))}
            <h3>Inactive Users</h3>
            {/* Inactivos */}
            {users
              .filter(user => user.status === 2)
              .map(user => (
                <UserItem
                  key={user.id}
                  user={user}
                  onClick={() => setSelectedUser(user)}
                  selected={selectedUser && selectedUser?.id === user.id}
                  isNotified={unreadUsers.includes(user.id)}
                >
                  {user.name} (Inactivo)
                  <NotificationIcon isNotified={unreadUsers.includes(user.id)} />
                </UserItem>
              ))}
          </UserList>
        </Sidebar>

        {/* Historial de mensajes */}
        <MainChat style={{ position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
            <h2>Chat</h2>
            {(selectedUser || selectedGroup) && (
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em', color: '#666' }}
                title="Search Messages"
              >
                <FaSearch />
              </button>
            )}
          </div>
          <h3>{selectedUser ? `Chatting with ${selectedUser.name}` : selectedGroup ? `Chatting in group ${userGroups.find(group => group.id === selectedGroup)?.name}` : 'No chat selected'}</h3>

          {isSearchOpen && (
            <SearchComponent
              onClose={() => setIsSearchOpen(false)}
              onSearch={handleSearch}
              results={searchResults}
              isLoading={isSearching}
            />
          )}

          <MessageList ref={messageListRef}>
            {selectedUser || selectedGroup ? (
              Array.isArray(messages) && messages.length > 0 ? (
                messages.map((message, index) => (
                  <MessageItem
                    key={message.id}
                    ownMessage={message.sender_id === authenticatedUserId}
                  >
                    <div style={{ marginBottom: '5px' }}>
                      {message.sender_id === authenticatedUserId ? 'You' : getUsernameById(message.sender_id)}
                      {' - '}
                      <strong>
                        {message.created_at
                          ? new Date(message.created_at).toLocaleString('en-US', { timeZone: 'America/Chicago' })
                          : 'No Date Available'}
                      </strong>
                    </div>

                    {message.reply_to && (
                      <ReplyContainer>
                        <span>
                          Reply To{' '}
                          <strong>{messages.find(m => m.id === message.reply_to)?.sender_id === authenticatedUserId
                            ? 'You'
                            : getUsernameById(messages.find(m => m.id === message.reply_to)?.sender_id || null)}
                            :{' '}
                          </strong>
                          {messages.find(m => m.id === message.reply_to)?.content || 'Mensaje no encontrado'}
                        </span>
                      </ReplyContainer>
                    )}


                    {/* Input de edición o contenido del mensaje */}
                    {editingMessageId === message.id ? (
                      <div style={{ marginTop: '10px' }}>
                        <input
                          type="text"
                          value={editedContent}
                          onChange={(e) => setEditedContent(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ccc'
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSaveEdit(message.id);
                            } else if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                        />
                        <div style={{ marginTop: '5px' }}>
                          <button
                            onClick={() => handleSaveEdit(message.id)}
                            style={{
                              marginRight: '5px',
                              padding: '5px 10px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            SAVE
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel

                          </button>
                        </div>
                      </div>
                    ) : message.is_deleted ? (
                      <div>
                        <span style={{ color: '#888', fontStyle: 'italic' }}>
                          🚫 Message deleted
                        </span>
                      </div>
                    ) : (
                      <div>
                        {message.file_url && (
                          <AttachmentCard
                            href={`${apiUrl}${message.file_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={message.file_name}
                          >
                            <span className="file-icon">{getFileIcon(message.file_type, message.file_name)}</span>
                            <span className="file-info">
                              <span className="file-name">{message.file_name}</span>
                              {message.file_size ? <span className="file-size">{formatFileSize(message.file_size)}</span> : null}
                            </span>
                          </AttachmentCard>
                        )}
                        {message.content && <span>{message.content}</span>}
                        {!!message.is_edited && (
                          <span style={{
                            marginLeft: '10px',
                            fontSize: '12px',
                            color: '#888',
                            fontStyle: 'italic'
                          }}>
                            Edited
                          </span>
                        )}
                      </div>
                    )}

                    {/* Botones de acción solo para el autor del mensaje */}
                    {editingMessageId !== message.id && !message.is_deleted && (
                      <div style={{ marginTop: '5px' }}>
                        {/* Botón Edit solo para mensajes propios */}
                        {message.sender_id === authenticatedUserId && (
                          <button
                            onClick={() => handleStartEdit(message)}
                            style={{
                              marginRight: '10px',
                              background: 'none',
                              border: 'none',
                              color: '#FF9800',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ✏️ Edit
                          </button>
                        )}

                        {/* Botón Delete solo para mensajes propios con archivo */}
                        {message.sender_id === authenticatedUserId && message.file_url && (
                          <button
                            onClick={() => handleDeleteMessage(message.id)}
                            style={{
                              marginRight: '10px',
                              background: 'none',
                              border: 'none',
                              color: '#f44336',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        )}

                        {/* Botón Reply para TODOS los mensajes */}
                        <button
                          onClick={() => setReplyingTo(message)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#34b7f1',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ↩️ Reply
                        </button>
                      </div>
                    )}
                  </MessageItem>
                ))
              ) : (
                <p>No messages available.</p>
              )
            ) : (
              <p>Select a user or group to start chatting.</p>
            )}
            <div ref={messagesEndRef} />
          </MessageList>

          {(selectedUser || selectedGroup) && (
            <InputContainer>
              {replyingTo && (
                <ReplyContainer>
                  <span>Respondiendo a: {replyingTo.content}</span>
                  <button onClick={() => setReplyingTo(null)}>X</button>
                </ReplyContainer>
              )}
              {selectedFile && (
                <FilePreviewChip>
                  <span className="file-icon">{getFileIcon(selectedFile.type, selectedFile.name)}</span>
                  <span className="file-name">{selectedFile.name}</span>
                  <span className="file-size">{formatFileSize(selectedFile.size)}</span>
                  <button type="button" title="Quitar" onClick={clearSelectedFile}>
                    <FaTimes />
                  </button>
                </FilePreviewChip>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                style={{ display: 'none' }}
              />
              <AttachButton
                type="button"
                title="Adjuntar documento (PDF, Word, Excel, TXT)"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                <FaPaperclip />
              </AttachButton>
              <MessageInput
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a Message..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (selectedUser) {
                      handleSendMessage();
                    } else if (selectedGroup) {
                      handleSendGroupMessage();
                    } else {
                      console.error('No user or group selected.');
                    }
                  }
                }}
              />
              <SendButton
                onClick={() => {
                  if (selectedUser) {
                    handleSendMessage();
                  } else if (selectedGroup) {
                    handleSendGroupMessage();
                  } else {
                    console.error('No user or group selected.');
                  }
                }}
              >
                Send
              </SendButton>
            </InputContainer>)}
        </MainChat>

        {/* Lista de grupos */}
        <InfoPanel>
          <GroupButton onClick={toggleModal}>
            Create Group
          </GroupButton>
          <h2>Groups</h2>
          <UserList>
            {userGroups.map(group => (
              <GroupItem
                key={group.id}
                selected={selectedGroup === group.id}
                onClick={() => setSelectedGroup(group.id)}
                isNotified={unreadGroups.includes(group.id)}

              >
                {group.name}

                <NotificationIcon isNotified={unreadGroups.includes(group.id)} /> {/* Muestra el icono de notificación si el grupo tiene mensajes no leídos */}
                <span
                  style={{ marginLeft: '8px', cursor: 'pointer' }}
                  title="Ver participantes"
                  onClick={(e) => {
                    e.stopPropagation(); // para que no dispare selección de grupo
                    handleOpenGroupParticipants(group);
                  }}
                >
                  👥
                </span>
              </GroupItem>
            ))}
          </UserList>
        </InfoPanel>
      </div>

      {/* Modal para crear grupo */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={toggleModal}
        contentLabel="Create Group"
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
          },
        }}
      >
        <h2>Create Group</h2>
        <form onSubmit={(e) => { e.preventDefault(); handleCreateGroup(); }}>
          <div>
            <label>Group Name</label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name:"
              required
            />
          </div>
          <div>
            <label>Select Users</label>
            <ul>
              {users.filter(user => user.id !== authenticatedUserId).map(user => (
                <li key={user.id}>
                  <input
                    type="checkbox"
                    checked={selectedGroupUsers.includes(user.id)}
                    onChange={() => handleGroupUserSelection(user.id)}
                  />
                  {user.name}
                </li>
              ))}
            </ul>
          </div>
          <button type="submit">Create Group</button>
          <button type="button" onClick={toggleModal}>Cancel</button>
        </form>
      </Modal>

      {/* Coloca ToastContainer al final del componente */}

      <ToastContainer />

      <GroupParticipantsModal
        isOpen={isGroupParticipantsModalOpen}
        onClose={() => setIsGroupParticipantsModalOpen(false)}
        participants={groupParticipants}
        groupName={selectedGroupName}
      />

    </ChatContainer>
  );
};

export default ChatPage;